import type { CopyPricePreviewItem } from '@/api';
import type { ColumnAttributes } from '@/components/table/table-types';
import type { RefObject } from 'react';

export type CopyPricePreviewTableProps = {
  items: CopyPricePreviewItem[];
  isExecuting: boolean;
  wouldCopyCount: number;
  triggerRef: RefObject<HTMLElement | null>;
  onConfirmCopy: () => void;
};

export type CopyPricePreviewItemForDisplay = Omit<
  CopyPricePreviewItem,
  'launchStatus' | 'pricingStatus' | 'isSource' | 'rrp' | 'retailMargin' | 'lp'
> & {
  launchStatus: string;
  pricingStatus: string;
  isSource: string;
  rrp: string;
  retailMargin: string;
  lp: string;
};

export type ColumnCategory = 'priceList' | 'productData' | 'copy' | 'copyData';

export type ColumnGroup = {
  id: string;
  header: string;
  columnCategory: ColumnCategory;
  columnKeys: (keyof CopyPricePreviewItem)[];
};

export const columnGroups: ColumnGroup[] = [
  {
    id: 'price-list',
    header: 'Price List',
    columnCategory: 'priceList',
    columnKeys: ['priceUnitId', 'priceUnitName'],
  },
  {
    id: 'product-data',
    header: 'Product Data',
    columnCategory: 'productData',
    columnKeys: [
      'productId',
      'productName',
      'theme',
      'launchStatus',
      'pricingStatus',
    ],
  },
  {
    id: 'copy',
    header: '',
    columnCategory: 'copy',
    columnKeys: ['isSource'],
  },
  {
    id: 'copy-data',
    header: 'Copy Data',
    columnCategory: 'copyData',
    columnKeys: ['rrp', 'retailMargin', 'lp'],
  },
];

export const columnData: Record<keyof CopyPricePreviewItem, ColumnAttributes> =
  {
    priceUnitId: {
      label: 'Price List',
      size: 100,
      alignment: 'left',
    },
    priceUnitName: {
      label: 'Price List Description',
      size: 200,
      alignment: 'left',
    },
    productId: {
      label: 'SKU ID',
      size: 90,
      alignment: 'left',
    },
    productName: {
      label: 'SKU Description',
      size: 200,
      alignment: 'left',
    },
    theme: {
      label: 'Theme',
      size: 140,
      alignment: 'left',
    },
    launchStatus: {
      label: 'Status',
      size: 90,
      alignment: 'left',
    },
    pricingStatus: {
      label: 'Price Status',
      size: 110,
      alignment: 'left',
    },
    isSource: {
      label: 'Copy',
      size: 80,
      alignment: 'left',
    },
    rrp: {
      label: 'RRP',
      size: 100,
      alignment: 'right',
    },
    retailMargin: {
      label: 'RM',
      size: 100,
      alignment: 'right',
    },
    lp: {
      label: 'LP',
      size: 100,
      alignment: 'right',
    },
  };

export const numericFilterColumns = new Set<keyof CopyPricePreviewItem>([
  'rrp',
  'retailMargin',
  'lp',
]);

export const percentageColumns = new Set<keyof CopyPricePreviewItem>([
  'retailMargin',
]);
